/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { ProviderOperationsMetadataOperations } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { AuthorizationManagementClient } from "../authorizationManagementClient";
import {
  ProviderOperationsMetadata,
  ProviderOperationsMetadataListNextOptionalParams,
  ProviderOperationsMetadataListOptionalParams,
  ProviderOperationsMetadataListResponse,
  ProviderOperationsMetadataGetOptionalParams,
  ProviderOperationsMetadataGetResponse,
  ProviderOperationsMetadataListNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing ProviderOperationsMetadataOperations operations. */
export class ProviderOperationsMetadataOperationsImpl
  implements ProviderOperationsMetadataOperations {
  private readonly client: AuthorizationManagementClient;

  /**
   * Initialize a new instance of the class ProviderOperationsMetadataOperations class.
   * @param client Reference to the service client
   */
  constructor(client: AuthorizationManagementClient) {
    this.client = client;
  }

  /**
   * Gets provider operations metadata for all resource providers.
   * @param options The options parameters.
   */
  public list(
    options?: ProviderOperationsMetadataListOptionalParams
  ): PagedAsyncIterableIterator<ProviderOperationsMetadata> {
    const iter = this.listPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(options, settings);
      }
    };
  }

  private async *listPagingPage(
    options?: ProviderOperationsMetadataListOptionalParams,
    settings?: PageSettings
  ): AsyncIterableIterator<ProviderOperationsMetadata[]> {
    let result: ProviderOperationsMetadataListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(continuationToken, options);
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    options?: ProviderOperationsMetadataListOptionalParams
  ): AsyncIterableIterator<ProviderOperationsMetadata> {
    for await (const page of this.listPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Gets provider operations metadata for the specified resource provider.
   * @param resourceProviderNamespace The namespace of the resource provider.
   * @param options The options parameters.
   */
  get(
    resourceProviderNamespace: string,
    options?: ProviderOperationsMetadataGetOptionalParams
  ): Promise<ProviderOperationsMetadataGetResponse> {
    return this.client.sendOperationRequest(
      { resourceProviderNamespace, options },
      getOperationSpec
    );
  }

  /**
   * Gets provider operations metadata for all resource providers.
   * @param options The options parameters.
   */
  private _list(
    options?: ProviderOperationsMetadataListOptionalParams
  ): Promise<ProviderOperationsMetadataListResponse> {
    return this.client.sendOperationRequest({ options }, listOperationSpec);
  }

  /**
   * ListNext
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    nextLink: string,
    options?: ProviderOperationsMetadataListNextOptionalParams
  ): Promise<ProviderOperationsMetadataListNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/providers/Microsoft.Authorization/providerOperations/{resourceProviderNamespace}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ProviderOperationsMetadata
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion1, Parameters.expand],
  urlParameters: [Parameters.$host, Parameters.resourceProviderNamespace],
  headerParameters: [Parameters.accept],
  serializer
};
const listOperationSpec: coreClient.OperationSpec = {
  path: "/providers/Microsoft.Authorization/providerOperations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ProviderOperationsMetadataListResult
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  queryParameters: [Parameters.apiVersion1, Parameters.expand],
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept],
  serializer
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ProviderOperationsMetadataListResult
    },
    default: {
      bodyMapper: Mappers.ErrorResponse
    }
  },
  urlParameters: [Parameters.$host, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer
};
